import { useEffect, useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import get from 'lodash/get';

import {
  pagesQuery,
  themeQuery,
  datamodelQuery,
  datamodelsQuery,
} from '@queries';
import { datamodelIdFromType } from '@helpers';
import { FETCH_STATUS } from '@constants';
// Improved version of https://usehooks.com/useOnClickOutside/
export const useClickOutside = (ref, handler) => {
  useEffect(() => {
    let startedInside = false;
    let startedWhenMounted = false;

    const listener = (event) => {
      // Do nothing if `mousedown` or `touchstart` started inside ref element
      if (startedInside || !startedWhenMounted) return;
      // Do nothing if clicking ref's element or descendent elements
      if (!ref.current || ref.current.contains(event.target)) return;

      handler(event);
    };

    const validateEventStart = (event) => {
      startedWhenMounted = ref.current;
      startedInside = ref.current && ref.current.contains(event.target);
    };

    document.addEventListener('mousedown', validateEventStart);
    document.addEventListener('touchstart', validateEventStart);
    document.addEventListener('click', listener);

    return () => {
      document.removeEventListener('mousedown', validateEventStart);
      document.removeEventListener('touchstart', validateEventStart);
      document.removeEventListener('click', listener);
    };
  }, [ref, handler]);
};

export const usePageTemplates = (
  siteID,
  themeID,
  pageType = 'template',
  pageFields,
) => {
  // custom hook to get the page templates available based on its site and theme

  // get theme
  const { data: themeData, isPending: themeIsPending } = useQuery(
    themeQuery(themeID),
  );

  // get pages for current site (for page templates)
  const { data: pagesData, isPending: pagesIsPending } = useQuery(
    pagesQuery({
      order_by: 'name',
      site_id: siteID,
      type: pageType,
      ...(pageFields ? { _fields: pageFields.join(',') } : {}),
    }),
  );

  // Site ID of the parent site via theme data
  const parentSiteId = themeData?.site_override?.en;

  // only load parent pages if its not for the current site (i.e. template pages)
  const hasParentPages =
    parentSiteId && siteID && parseInt(parentSiteId) != parseInt(siteID);

  // get parent pages (for page templates)
  const { data: parentPagesData, isPending: parentPagesIsPending } = useQuery(
    pagesQuery(
      {
        order_by: 'name',
        site_id: parentSiteId,
        type: pageType,
        ...(pageFields ? { _fields: pageFields.join(',') } : {}),
      },
      {
        enabled: !!hasParentPages,
      },
    ),
  );

  let combinedPagesList = null;
  if (pagesData && themeData) {
    combinedPagesList = pagesData.objects;
    if (hasParentPages && parentPagesData) {
      combinedPagesList = pagesData.objects.concat(parentPagesData.objects);
    }
  }
  if (combinedPagesList && pageType == 'template') {
    // filter to just templates with proper names filled in
    combinedPagesList = combinedPagesList.filter((p) => p.template_name);
  }

  return {
    data: combinedPagesList,
    isPending:
      pagesIsPending ||
      themeIsPending ||
      (hasParentPages && parentPagesIsPending),
    parentSiteId,
  };
};

// returns the datamodel data, related datamodels, and related datamodels ready status
export const useDatamodel = (fieldType) => {
  const shouldResolveType =
    typeof fieldType === 'string' && fieldType.length > 0;

  const { data: datamodelsData, isPending: datamodelsIsPending } = useQuery(
    datamodelsQuery({ limit: 200 }),
  );

  const datamodelID =
    shouldResolveType && datamodelsData
      ? datamodelIdFromType(datamodelsData?.objects, fieldType)
      : null;

  const shouldFetchDatamodel = shouldResolveType && !!datamodelID;

  const { data: datamodelData, isPending: datamodelIsPending } = useQuery(
    datamodelQuery(datamodelID, {
      enabled: shouldFetchDatamodel,
    }),
  );

  const relatedDatamodelQueries =
    shouldFetchDatamodel && datamodelData
      ? datamodelData.fields
          .filter((field) => Number.isInteger(field.related_datamodel_id))
          .map((field) => datamodelQuery(field.related_datamodel_id))
      : [];

  const relatedDatamodels = useQueries({
    enabled:
      relatedDatamodelQueries?.length > 0 &&
      shouldFetchDatamodel &&
      !datamodelIsPending,
    queries: relatedDatamodelQueries,
  });

  const relatedDatamodelsReady = shouldFetchDatamodel
    ? relatedDatamodels?.every((query) => !query.isPending)
    : true;

  const relatedPending = shouldFetchDatamodel
    ? relatedDatamodels?.some((query) => query.isPending)
    : false;

  const isPending =
    datamodelsIsPending ||
    (shouldFetchDatamodel && datamodelIsPending) ||
    (shouldFetchDatamodel && relatedPending);

  return {
    datamodelsData,
    datamodelData: shouldFetchDatamodel ? datamodelData : null,
    isPending,
    datamodelID,
    relatedDatamodels: shouldFetchDatamodel ? relatedDatamodels : [],
    relatedDatamodelsReady,
  };
};

const RELATED_MODEL_LIST_COUNT_ERROR_TYPE =
  'related_model_list_count_validation';

const toInt = (value) => {
  if (typeof value === 'number') return Math.trunc(value);
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && !Number.isNaN(parsed) ? parsed : undefined;
};

/**
 * Enforce min/max item counts for "related_model_list" fields.
 *
 * - Validates min/max only when `required === true` OR the user started adding items (`count > 0`)
 * - Sets/clears errors in a guarded way to avoid render loops
 */
export const useRelatedMinMaxValidation = ({
  fields,
  namePrefix,
  watch,
  errors,
  setError,
  clearErrors,
}) => {
  const names = useMemo(() => {
    return (fields || []).map((f) => `${namePrefix}.${f.slug}`);
  }, [fields, namePrefix]);

  const values = watch(names);

  useEffect(() => {
    (fields || []).forEach((field, idx) => {
      const fieldName = `${namePrefix}.${field.slug}`;
      const value = values?.[idx];
      const count = Array.isArray(value) ? value.length : 0;

      const min = toInt(field.min_items);
      const max = toInt(field.max_items);
      const hasMin = typeof min === 'number' && min > 0;
      const hasMax = typeof max === 'number' && max > 0;
      const logicalMin = hasMin ? min : 1;

      const shouldValidateMin = field.required || count > 0;
      const shouldValidateMax = (field.required || count > 0) && hasMax;

      let desiredMessage;
      if (shouldValidateMin && count < logicalMin) {
        desiredMessage = `Add at least ${logicalMin} item(s).`;
      } else if (shouldValidateMax && count > max) {
        desiredMessage = `You can add at most ${max} item(s).`;
      }

      const currentError = get(errors, fieldName);
      const isOurErrorType =
        currentError?.type === RELATED_MODEL_LIST_COUNT_ERROR_TYPE;

      if (desiredMessage) {
        if (currentError?.message !== desiredMessage || !isOurErrorType) {
          setError(fieldName, {
            type: RELATED_MODEL_LIST_COUNT_ERROR_TYPE,
            message: desiredMessage,
          });
        }
        return;
      }

      if (isOurErrorType) {
        clearErrors(fieldName);
      }
    });
  }, [clearErrors, errors, fields, namePrefix, setError, values]);
};
